import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiSearch, FiFilter, FiClock, FiCheckCircle, FiAlertCircle, 
  FiServer, FiUser, FiCode, FiMail, FiChevronRight, 
  FiRefreshCw, FiLoader, FiDatabase, FiInbox, FiSend 
} = FiIcons;

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const Logs = () => {
  const [activeTab, setActiveTab] = useState('send'); // 'send','error','queue'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const callGateway = async (action, payload = {}) => {
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, payload })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Gateway Error');
      return result;
    } catch (err) {
      console.error(`Gateway [${action}] failed:`, err.message);
      throw err;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'queue') {
        const res = await callGateway('list_email_queue');
        setQueue(res.data || []);
      } else {
        const res = await callGateway('list_delivery_logs');
        setLogs(res.data || []);
      }
      setIsLive(true);
    } catch (err) {
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async (id) => {
    setProcessingId(id);
    try {
      const res = await callGateway('send_queued_email', { queue_id: id });
      showToast(res.message);
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const filteredLogs = logs.filter(log => {
    const matchesSearch = (log.recipient || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (log.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'send' ? log.status === 'success' : 
                       activeTab === 'error' ? log.status === 'error' : false;
    return matchesSearch && matchesTab;
  });

  const filteredQueue = queue.filter(item => 
    (item.to_email || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (item.subject || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderLogsTable = () => (
    <div className="overflow-x-auto relative min-h-[200px]">
      {loading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
          <SafeIcon icon={FiLoader} className="text-2xl text-blue-600 animate-spin" />
        </div>
      )}
      {!isLive ? (
        <div className="p-12 text-center text-slate-400 italic text-sm">
          <SafeIcon icon={FiAlertCircle} className="mx-auto text-3xl mb-2 opacity-20" />
          Logs viewing requires live PocketBase connection.
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="p-12 text-center text-slate-400 italic text-sm">
          <SafeIcon icon={FiInbox} className="mx-auto text-3xl mb-2 opacity-20" />
          No {activeTab} logs found.
        </div>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
              <th className="px-6 py-4">Timestamp</th>
              <th className="px-6 py-4">Event</th>
              <th className="px-6 py-4">Recipient</th>
              <th className="px-6 py-4">Subject</th>
              <th className="px-6 py-4">Provider</th>
              <th className="px-6 py-4">Status</th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-slate-50">
            {filteredLogs.map(log => (
              <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-[11px] text-slate-400 font-mono">
                  {new Date(log.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </td>
                <td className="px-6 py-4">
                  <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wider">{log.event_type.replace(/_/g, ' ')}</p>
                </td>
                <td className="px-6 py-4 font-medium text-slate-600">{log.recipient}</td>
                <td className="px-6 py-4 text-xs text-slate-500 truncate max-w-[200px]">{log.subject}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded uppercase">{log.provider}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${log.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {log.status === 'success' ? 'Delivered' : 'Failed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderQueueTable = () => (
    <div className="overflow-x-auto relative min-h-[200px]">
      {loading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
          <SafeIcon icon={FiLoader} className="text-2xl text-blue-600 animate-spin" />
        </div>
      )}
      {!isLive ? (
        <div className="p-12 text-center text-slate-400 italic text-sm">
          <SafeIcon icon={FiAlertCircle} className="mx-auto text-3xl mb-2 opacity-20" />
          Queue viewing requires live PocketBase connection.
        </div>
      ) : filteredQueue.length === 0 ? (
        <div className="p-12 text-center text-slate-400 italic text-sm">
          <SafeIcon icon={FiInbox} className="mx-auto text-3xl mb-2 opacity-20" />
          Email queue is currently empty.
        </div>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
              <th className="px-6 py-4">Created</th>
              <th className="px-6 py-4">Recipient</th>
              <th className="px-6 py-4 text-center">Status</th>
              <th className="px-6 py-4">Subject</th>
              <th className="px-6 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-slate-50">
            {filteredQueue.map(item => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-[11px] text-slate-400 font-mono italic">
                  {new Date(item.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </td>
                <td className="px-6 py-4 font-bold text-slate-700">{item.to_email}</td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                    item.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 
                    item.status === 'failed' ? 'bg-red-50 text-red-600' :
                    'bg-blue-50 text-blue-600 border border-blue-100'
                  }`}>
                    {item.status}
                  </span>
                  {item.error_message && (
                    <p className="text-[8px] text-red-500 mt-1 truncate max-w-[100px] mx-auto">{item.error_message}</p>
                  )}
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 truncate max-w-xs">{item.subject}</td>
                <td className="px-6 py-4 text-right cursor-default">
                  {item.status !== 'sent' && (
                    <button 
                      onClick={() => handleSendNow(item.id)}
                      disabled={processingId === item.id}
                      className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      title="Send Now"
                    >
                      <SafeIcon icon={processingId === item.id ? FiLoader : FiSend} className={processingId === item.id ? 'animate-spin' : ''} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Operational Logs</h2>
          <p className="text-sm text-slate-500">Real-time visibility into gateway delivery and queue.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
            <button onClick={() => setActiveTab('send')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'send' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}>Send Logs</button>
            <button onClick={() => setActiveTab('error')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'error' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}>Error Logs</button>
            <button onClick={() => setActiveTab('queue')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'queue' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}>Draft Queue</button>
          </div>
          <button onClick={fetchData} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2 bg-slate-50 rounded-xl border border-slate-100">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input 
            type="text" 
            placeholder="Search by recipient or subject..." 
            className="w-full py-2 bg-transparent text-sm outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {activeTab === 'queue' ? renderQueueTable() : renderLogsTable()}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiIcons.FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs text-blue-800 leading-relaxed font-bold">
            {activeTab === 'queue' ? 'Draft Queue (PB-08A)' : 'Delivery Audit (PB-08A)'}
          </p>
          <p className="text-xs text-blue-800/80 leading-relaxed">
            {activeTab === 'queue' 
              ? 'Draft emails created via API appear here. You can manually process them using the send button.' 
              : 'Delivery events are automatically logged when emails are queued, sent, or if they fail through the gateway.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Logs;